/**
 * Тексты-подсказки и примеры формы СОУТ (canon service-sout «Подсказки у полей формы»).
 * Перенесены из формы дословно; держим в одном месте, чтобы переиспользовать в шагах визарда.
 */

/** Подсказки-примеры у полей. */
export const SOUT_HINTS = {
  inn: '10 или 12 цифр',
  nameFull: 'Например: ООО «Ромашка»',
  /**
   * Подсказка ПОД полем «Фактический адрес рабочего места» формы заказа СОУТ (centrika-docs!92,
   * `page.create-sout-order.new-workplace` → «Фактический адрес рабочего места») — дословно.
   * Пример корректного адреса при этом никуда не делся: он переехал в placeholder ВНУТРИ поля
   * (`SOUT_ACTUAL_ADDRESS_PLACEHOLDER`) — тот же паттерн, что у «Оборудование»/«Материалы»
   * (подсказка под полем + отдельный placeholder). Требование задаёт только подсказку и про
   * placeholder молчит, поэтому существующий пример сохранён как есть.
   */
  actualAddress: 'Будет указан в протоколе замеров',
  equipment: 'Например: персональный компьютер, токарный станок…',
  materials: 'Сырьё и материалы на рабочем месте',
  snils: '11 цифр, например 112-233-445 95',
  fio: 'Полностью, минимум 3 слова — без инициалов',
  shiftHours: 'Часы одной смены, больше 0',
  weekHours: 'Часов в неделю, больше 0',
  rmNumber: 'Индивидуальный № рабочего места из прошлой спецоценки',
  transportYear: '4 цифры, например 2020',
  /** Placeholder гос. номера ТС (IC-2, §решение 3) — дословно из канона `sout.html`. */
  transportPlate: 'А123ВС77',
} as const

/**
 * Метки полей блока «Идентификация» (p3-sout-workplaces §5, Волна 6) — ДОСЛОВНО из текста ТЗ
 * `Правки_2_для_разработчика.md`. Заменяют более ранний IC-2-канон (с маркером «*» и текстом
 * «строго по штатному расписанию, будет в карте СОУТ») — текст ТЗ этой волны приоритетнее
 * (Resolved №4, `docs/changes-3/...` §17).
 */
export const SOUT_WORKPLACE_FIELD_LABELS = {
  department: 'Структурное подразделение',
  position: 'Должность (как в штатном расписании)',
} as const

/**
 * Подсказка о составе комиссии в блоке «Члены комиссии» (sout-commission-odd, заказчица 15.10.2026;
 * 426-ФЗ ст. 9): правило видно до ошибки. Председатель входит в счёт.
 */
export const SOUT_COMMISSION_ODD_HINT =
  'Вместе с председателем в комиссии должно быть нечётное число человек: 1, 3, 5…'

/** Врезка-предупреждение о влиянии данных на результат СОУТ. */
export const SOUT_WARNING =
  'Будьте внимательны при заполнении! Данные напрямую повлияют на результаты СОУТ.'

/** Стартовый чек-лист «Что подготовить». */
export const SOUT_CHECKLIST: string[] = [
  'Реквизиты организации — при необходимости поправьте на первом шаге',
  'Состав комиссии: один председатель, члены комиссии — при необходимости',
  'Ответственного за СОУТ: ФИО, должность, телефон, email',
  'По каждому рабочему месту: подразделение, адрес, должность, СНИЛС сотрудника',
  'Режим работы, оборудование и материалы на местах',
  'Сведения о прошлой спецоценке, если она проводилась',
]

/** Подсказка про автосохранение черновика. */
export const SOUT_AUTOSAVE_HINT =
  'Черновик сохраняется автоматически — заполнение можно прервать и продолжить позже.'

/**
 * Подсказка на шаге «Организация» (design D1, §7.2, A1): реквизиты правит клиент —
 * правка применяется и к профилю компании, и к снимку этого заказа, без модерации.
 */
export const SOUT_REQUISITES_HINT =
  'Реквизиты можно поправить здесь — изменения применятся и к профилю компании, и к этому заказу.'

// --- Шаг «Рабочие места» (p3-sout-workplaces, ТЗ `Правки_2_для_разработчика.md`) ---

/**
 * Заголовок информационной плашки под индикатором шагов (§1). Способов стало четыре — добавилась
 * загрузка «Анкеты и формы СОУТ» (order-import-legacy-forms).
 */
export const SOUT_WORKPLACES_INTRO_TITLE = 'Добавить рабочие места можно четырьмя способами'

/**
 * Способы добавления рабочих мест — нумерованный список в информационной плашке
 * (centrika-docs, component.create-sout-order.workplaces, MR !9). Способ «Копия» переехал
 * сюда из отдельной подсказки под списком рабочих мест, которой больше нет.
 * p3-critical-fixes (№3, следствие): фраза «для рабочих мест без сотрудника» убрана — в заказ
 * СОУТ строки без сотрудника не добавляются.
 */
export const SOUT_WORKPLACES_INTRO_ITEMS: ReadonlyArray<{ name: string; text: string }> = [
  {
    name: '«Из структуры компании»',
    text: 'подразделение, должность и ФИО подставятся сами.',
  },
  {
    name: '«Вручную»',
    text: 'для новых сотрудников, которых ещё нет в кабинете.',
  },
  {
    name: '«Копия»',
    text:
      'похожие рабочие места удобно размножать кнопкой «Копия»: данные существующего ' +
      'рабочего места повторятся, а ФИО и СНИЛС нужно будет заполнить заново.',
  },
  {
    name: '«Загрузить из файла»',
    text: 'если анкета заполнена в Excel по форме Центрики («Анкета и форма СОУТ»).',
  },
]

// --- Загрузка «Анкеты и формы СОУТ» (order-import-legacy-forms) ---

/** Заголовок диалога загрузки. */
export const SOUT_IMPORT_TITLE = 'Загрузка анкеты СОУТ из файла'

/** Что за файл и что из него попадёт в заказ. */
export const SOUT_IMPORT_DESCRIPTION =
  'Выберите «Анкету и форму СОУТ» в Excel. Каждая строка листа «Замеры» станет рабочим местом ' +
  'заказа. ФИО в форме нет — места добавятся со СНИЛС и полом, а если СНИЛС есть в структуре ' +
  'компании, место свяжется с сотрудником. Колонка «Оснащение рабочего места» целиком попадёт в ' +
  '«Оборудование», в «Материалах» будет «Нет» — разнесите оборудование, материалы и служебный ' +
  'транспорт в карточке места. С листа «Анкета» переносятся состав комиссии и ответственный, если ' +
  'в заказе они ещё не заполнены.'

/** Подпись шапки страницы рабочего места (§4, дословно). */
export const SOUT_WORKPLACE_PAGE_SUBTITLE =
  'Данные — как в анкете СОУТ; чего не хватает, менеджер уточнит при проверке.'

/** Подпись шапки страницы «Добавить вручную» (§14): для нового рабочего места с сотрудником,
 *  которого ещё нет в структуре. p3-critical-fixes (№3г): сотрудник обязателен — упоминание
 *  «рабочего места без сотрудника» убрано. */
export const SOUT_WORKPLACE_MANUAL_PAGE_SUBTITLE =
  'Для нового рабочего места и сотрудника, которого ещё нет в кабинете. Если сотрудник уже ' +
  'есть в структуре компании — используйте кнопку «Из структуры компании».'

// --- Карточка рабочего места, блоки §5–§12 (Волна 6) — тексты подписей/подсказок дословно ---

/**
 * Ошибка под полем «СНИЛС», когда такой же СНИЛС уже указан в другом рабочем месте этого заказа
 * (centrika-docs!101, `page.create-sout-order.new-workplace` → СНИЛС → «Уникальность СНИЛС
 * в заказе») — дословно.
 */
export const SOUT_SNILS_ORDER_CONFLICT = 'В заказе уже указан сотрудник с таким СНИЛС'

/** Подпись под заголовком блока «Компенсации» (§9, дословно). */
export const SOUT_COMPENSATIONS_HINT = 'Отметьте, что сотрудник уже получает.'

/** Тексты шести чекбоксов компенсаций (§9, дословно, порядок как в ТЗ/скриншоте). */
export const SOUT_COMPENSATION_LABELS = {
  extraPay: 'Повышенная оплата труда',
  extraVacation: 'Дополнительный оплачиваемый отпуск',
  shortWeek: 'Сокращённая рабочая неделя',
  milk: 'Молоко или лечебно-профилактическое питание',
  earlyPension: 'Досрочная пенсия',
  medical: 'Обязательные медосмотры',
} as const

/**
 * Текст-заглушка при пустом списке несчастных случаев (дословно; centrika-docs,
 * page.create-sout-order.new-workplace, MR !21).
 */
export const SOUT_ACCIDENTS_EMPTY_TEXT = 'Несчастных случаев за последние 5 лет не зафиксировано.'

/**
 * Placeholder ВНУТРИ поля «Фактический адрес рабочего места» — прежний пример адреса
 * (IC-2-канон). После centrika-docs!92 подсказкой под полем стал текст «Будет указан в
 * протоколе замеров» (`SOUT_HINTS.actualAddress`), а пример остался примером — внутри поля.
 */
export const SOUT_ACTUAL_ADDRESS_PLACEHOLDER = 'Например: 350033, г. Краснодар, ул. Ставропольская, д. 41'

/** Placeholder поля «Кратко, что произошло» (§10, дословно из скриншота). */
export const SOUT_ACCIDENT_DESCRIPTION_PLACEHOLDER = 'Например: падение с высоты'

/** Подсказка под блоком «Оборудование и материалы» (§11, дословно). */
export const SOUT_EQUIPMENT_MATERIALS_HINT = 'Если оборудования или материалов нет — напишите «Нет».'

/** Placeholder поля «Оборудование» (centrika-docs, page.create-sout-order.new-workplace, MR !14). */
export const SOUT_EQUIPMENT_PLACEHOLDER =
  'Например: персональный компьютер, токарный станок, дрель, шуруповерт, плита газовая, электромясорубка. Если оборудование отсутствует — напишите «Нет».'

/** Placeholder поля «Материалы» (centrika-docs, page.create-sout-order.new-workplace, MR !14). */
export const SOUT_MATERIALS_PLACEHOLDER =
  'Например: хлорсодержащие средства, лакокрасочные материалы. Если материалы отсутствуют — напишите «Нет».'

/** Текст-заглушка при пустом списке служебного транспорта (§12, дословно). */
export const SOUT_TRANSPORT_EMPTY_TEXT = 'Служебных транспортных средств нет.'

/** Заголовок блока «Служебный транспорт» (centrika-docs, page.create-sout-order.new-workplace, MR !15). */
export const SOUT_TRANSPORT_TITLE = 'Служебные транспортные средства'

/** Текст акцентного инфо-блока транспорта (MR !15, дословно). */
export const SOUT_TRANSPORT_INFO =
  'Учитывается только транспорт, который либо числится на балансе предприятия, либо является служебным по договору аренды и/или компенсации ГСМ. Запрашиваемую информацию можно посмотреть в свидетельстве о регистрации транспортного средства. Если масса в СТС не указана (например, для складского транспорта), напишите в полях «Масса» цифру 27.'
